/**
 * Tenant-aware branding for PDFs, booking share messages, and export headers.
 */

import { fetchOrgCompanySettingsRow } from '../saas/lib/orgCompanySettings.js';
import { fetchTbSiteOrganization } from '../saas/lib/saasOrg.js';
import {
  getPlatformDefaultBranding,
  isSaasPlatform,
  resolveCompanyBranding,
  type CompanySettingsLike,
  type ResolvedCompanyBranding,
} from './companyBranding.js';
export { phoneTelHref } from './phoneTelHref.js';

export type DocumentBrandContext = {
  companyName: string;
  tagline: string;
  phone: string;
  email: string;
  website: string;
  websiteUrl: string;
  whatsappDigits: string;
  address: string;
  logoUrl: string | null;
  includeUsp: boolean;
};

function normalizeWebsiteUrl(website: string): string {
  const trimmed = website.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

function normalizeWebsiteDisplay(website: string): string {
  return website
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
}

export function documentBrandFromResolved(
  branding: ResolvedCompanyBranding,
  options?: { website?: string; tagline?: string },
): DocumentBrandContext {
  const defaults = getPlatformDefaultBranding();
  const companyName = branding.companyName?.trim() || defaults.companyName;
  const phone = branding.phone?.trim() || defaults.phone || '';
  const email = branding.email?.trim() || defaults.email || '';
  const whatsappDigits = phone.replace(/\D/g, '');
  const websiteDisplay = options?.website?.trim() || (isSaasPlatform() ? '' : 'www.toolsandbubbles.com');
  const websiteUrl = websiteDisplay ? normalizeWebsiteUrl(websiteDisplay) : '';
  const tagline =
    options?.tagline?.trim() ||
    branding.tagline?.trim() ||
    defaults.tagline ||
    'Professional Cleaning Services';

  return {
    companyName,
    tagline,
    phone,
    email,
    website: normalizeWebsiteDisplay(websiteDisplay),
    websiteUrl,
    whatsappDigits,
    address: branding.address?.trim() || defaults.address || '',
    logoUrl: branding.logoUrl ?? defaults.logoUrl,
    includeUsp: !isSaasPlatform(),
  };
}

export function getDefaultDocumentBrand(): DocumentBrandContext {
  return documentBrandFromResolved(getPlatformDefaultBranding());
}

/** Resolve org id for PDFs on toolsandbubbles.com (tb-site has no SaaS workspace org). */
export async function resolveDocumentOrganizationId(organizationId?: string | null): Promise<string | null> {
  const trimmed = organizationId?.trim();
  if (trimmed) return trimmed;
  const tbOrg = await fetchTbSiteOrganization();
  return tbOrg?.id ?? null;
}

export async function fetchDocumentBrandForOrg(
  organizationId?: string | null,
  options?: {
    orgName?: string | null;
    orgBranding?: Record<string, unknown> | null;
    website?: string;
  },
): Promise<DocumentBrandContext> {
  const resolvedOrgId = await resolveDocumentOrganizationId(organizationId);
  if (!resolvedOrgId) {
    return documentBrandFromResolved(getPlatformDefaultBranding(), {
      website: options?.website,
    });
  }
  try {
    const data = await fetchOrgCompanySettingsRow<CompanySettingsLike>(resolvedOrgId, '*');
    const resolved = resolveCompanyBranding(data, {
      orgName: options?.orgName,
      orgBranding: options?.orgBranding,
    });
    return documentBrandFromResolved(resolved, { website: options?.website });
  } catch {
    return getDefaultDocumentBrand();
  }
}

export function buildWaMeUrl(digits: string, message?: string): string {
  const wa = digits.replace(/\D/g, '').replace(/^\+/, '');
  if (!wa) return '';
  if (!message?.trim()) return `https://wa.me/${wa}`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(message)}`;
}

/** Branded WhatsApp deep link for public lead handoffs. */
export function buildBrandedWhatsAppHandoff(
  brand: Pick<DocumentBrandContext, 'companyName' | 'whatsappDigits'>,
  messageBody: string,
): string {
  const trimmed = messageBody.trim();
  const greeting = brand.companyName ? `Hi ${brand.companyName}, ` : 'Hi, ';
  const message = trimmed.startsWith('Hi ') ? trimmed : `${greeting}${trimmed}`;
  return buildWaMeUrl(brand.whatsappDigits, message);
}

export function documentBrandFooterLine(brand: DocumentBrandContext): string {
  const parts = [brand.companyName, brand.tagline].filter(Boolean);
  return parts.join('  —  ');
}

/** Safe segment for PDF download filenames (org company name). */
export function sanitizeDocumentPdfFilenameSegment(value: string): string {
  const cleaned = value
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned.slice(0, 48) || 'Company';
}

export function buildDocumentBrochurePdfFilename(brand: DocumentBrandContext, catalogLabel: string): string {
  const timestamp = new Date()
    .toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    .replace(/\s/g, '-');
  const prefix = sanitizeDocumentPdfFilenameSegment(brand.companyName);
  const label = sanitizeDocumentPdfFilenameSegment(catalogLabel);
  return `${prefix}-${label}-${timestamp}.pdf`;
}
